// Tiny, safe inline-markdown renderer for content editable via the admin dashboard.
// Supports **bold** and *italic* (single asterisk, not part of a double) and returns
// plain React nodes (strings / <strong> / <em>) — no dangerouslySetInnerHTML anywhere.
// Newlines are left untouched; the consumer is expected to render them with a
// `whitespace-pre-line` (or similar) class, exactly like it already does for plain text.

function parseItalicOnly(text, keyPrefix) {
  const nodes = [];
  const re = /\*(.+?)\*/g;
  let idx = 0;
  let match;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > idx) nodes.push(text.slice(idx, match.index));
    nodes.push(<em key={`${keyPrefix}-i-${key++}`}>{match[1]}</em>);
    idx = re.lastIndex;
  }
  if (idx < text.length) nodes.push(text.slice(idx));
  return nodes;
}

function parseInline(text, keyPrefix) {
  const nodes = [];
  // Bold (**...**) takes priority; italic (*...*) is matched where bold isn't.
  const re = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let idx = 0;
  let match;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > idx) nodes.push(text.slice(idx, match.index));
    if (match[1] !== undefined) {
      nodes.push(
        <strong key={`${keyPrefix}-b-${key++}`}>
          {parseItalicOnly(match[1], `${keyPrefix}-b-${key}`)}
        </strong>
      );
    } else {
      nodes.push(<em key={`${keyPrefix}-e-${key++}`}>{match[2]}</em>);
    }
    idx = re.lastIndex;
  }
  if (idx < text.length) nodes.push(text.slice(idx));
  return nodes;
}

/**
 * rich(text) -> string | Array<string | ReactElement>
 * Renders **bold** and *italic* as React elements. Returns the original value
 * untouched when it isn't a non-empty string containing "*".
 */
export function rich(text) {
  if (text === null || text === undefined) return text;
  const str = typeof text === "string" ? text : String(text);
  if (!str.includes("*")) return str;
  return parseInline(str, "rt");
}

// Block-level markers recognised by richBlocks() (case-insensitive), mapped to the Tailwind
// alignment class applied to the wrapping <div>.
const BLOCK_MARKERS = { zentriert: "text-center", blocksatz: "text-justify", rechts: "text-right" };

// Matches an open marker anywhere in the string (not just alone on its own line), capturing
// everything up to the matching close tag or, forgivingly, the end of the string.
const BLOCK_RE = /\[(zentriert|blocksatz|rechts)\]([\s\S]*?)(?:\[\/\1\]|$)/gi;

/**
 * richBlocks(text) -> string | Array<string | ReactElement>
 * Finds marker spans [zentriert]...[/zentriert], [blocksatz]...[/blocksatz] and
 * [rechts]...[/rechts] anywhere in the text (case-insensitive, not required to stand alone on
 * their own line) and wraps the enclosed text in a <div> with the corresponding alignment class.
 * Text outside markers renders exactly like rich(text) does. Inline bold/italic markup keeps
 * working everywhere, including inside blocks. An unclosed marker forgivingly consumes the rest
 * of the text as that block instead of erroring. A single leading/trailing newline directly
 * inside the block is trimmed, and a single newline right after the close tag is swallowed, so
 * that whitespace-pre-line rendering doesn't introduce stray blank lines around the block.
 */
export function richBlocks(text) {
  if (text === null || text === undefined) return text;
  const str = typeof text === "string" ? text : String(text);
  if (!str) return str;

  const nodes = [];
  let key = 0;
  let idx = 0;
  let match;

  const pushPlain = (chunk) => {
    if (!chunk) return;
    const rendered = rich(chunk);
    if (Array.isArray(rendered)) nodes.push(...rendered);
    else nodes.push(rendered);
  };

  BLOCK_RE.lastIndex = 0;
  while ((match = BLOCK_RE.exec(str)) !== null) {
    if (match.index > idx) pushPlain(str.slice(idx, match.index));

    const name = match[1].toLowerCase();
    let inner = match[2];
    // Trim a single leading/trailing newline immediately inside the marker.
    if (inner.startsWith("\n")) inner = inner.slice(1);
    if (inner.endsWith("\n")) inner = inner.slice(0, -1);

    nodes.push(
      <div key={`rb-${key++}`} className={BLOCK_MARKERS[name]}>
        {rich(inner)}
      </div>
    );

    idx = BLOCK_RE.lastIndex;
    // Swallow one newline immediately after the close tag.
    if (str[idx] === "\n") idx++;
    BLOCK_RE.lastIndex = idx;
  }
  if (idx < str.length) pushPlain(str.slice(idx));

  return nodes;
}

export default rich;
